/**
 * Config Loader Service
 * 
 * This service handles intelligent loading of saved configurations from URL parameters
 * and determines the appropriate step and component to display based on the config data.
 */

/**
 * Determines the cable type from a configuration object
 * @param {Object} config - The configuration object
 * @returns {string} - The cable type ('base', 'hybrid', 'wye', 'zip', 'hybrid-power-audio-audio')
 */
export function determineCableType(config) {
  if (!config) return 'base';
  
  // Check for specific cable styles first
  if (config.style === 'hybrid') {
    return 'hybrid';
  } else if (config.style === 'wye') {
    return 'wye';
  } else if (config.style === 'zip' || config.style === 'zip-audio-audio') {
    return 'zip';
  } else if (config.style === 'hybrid-power-audio-audio') {
    return 'hybrid-power-audio-audio';
  }
  
  // Check for connector structure to determine type
  if (config.hybridConnectors && Object.keys(config.hybridConnectors.connectors || {}).length > 0) {
    // Check if it's a power+audio hybrid by looking for power connectors
    const connectors = config.hybridConnectors.connectors;
    const hasPowerConnector = Object.values(connectors).some(conn => 
      conn.purpose && conn.purpose.includes('power')
    );
    
    if (hasPowerConnector) {
      return 'hybrid-power-audio-audio';
    } else {
      return 'hybrid';
    }
  }
  
  // Check for wye-specific structure
  if (config.connectors && Object.keys(config.connectors).length > 2) {
    // More than 2 connectors suggests a wye configuration
    return 'wye';
  }
  
  // Default to base/standard cable
  return 'base';
}

/**
 * Determines the appropriate step to navigate to based on config completeness
 * @param {Object} config - The configuration object
 * @param {string} cableType - The determined cable type
 * @returns {number} - The step index (0-4)
 */
export function determineStepFromConfig(config, cableType) {
  if (!config) return 0;
  
  // Step 0: Cable Selection - always start here if no type selected
  if (!config.type) return 0;
  
  // Step 1: Style Selection - if type but no style
  if (!config.style) return 1;
  
  // Step 2: Connectors - if we have type and style but no connectors configured
  const hasConnectors = checkConnectorCompleteness(config, cableType);
  if (!hasConnectors) return 2;
  
  // Step 3: Length - if we have connectors but no length configured
  const hasLength = checkLengthCompleteness(config, cableType);
  if (!hasLength) return 3;
  
  // Step 4: Finishing - if we have connectors and length but no finishing
  const hasFinishing = checkFinishingCompleteness(config);
  if (!hasFinishing) return 4;
  
  // If everything is complete, go to review (step 4)
  return 4;
}

/**
 * Checks if connectors are properly configured for the given cable type
 * @param {Object} config - The configuration object
 * @param {string} cableType - The cable type
 * @returns {boolean} - True if connectors are configured
 */
function checkConnectorCompleteness(config, cableType) {
  switch (cableType) {
    case 'base':
      return !!(config.connectors?.endA?.connector && config.connectors?.endB?.connector);
    
    case 'hybrid':
    case 'hybrid-power-audio-audio':
      if (!config.hybridConnectors?.connectors) return false;
      const hybridConnectors = config.hybridConnectors.connectors;
      // Check if at least the main connectors (F, G) are selected
      return !!(hybridConnectors.F?.connector && hybridConnectors.G?.connector);
    
    case 'wye':
      if (!config.connectors) return false;
      // Check if we have the main connectors for a wye
      const wyeKeys = Object.keys(config.connectors);
      return wyeKeys.length >= 2 && wyeKeys.some(key => config.connectors[key]?.connector);
    
    case 'zip':
      if (!config.connectors) return false;
      // Check for zip-specific connector structure
      const zipKeys = Object.keys(config.connectors);
      return zipKeys.length >= 2 && zipKeys.some(key => config.connectors[key]?.connector);
    
    default:
      return false;
  }
}

/**
 * Checks if length is properly configured
 * @param {Object} config - The configuration object
 * @param {string} cableType - The cable type
 * @returns {boolean} - True if length is configured
 */
function checkLengthCompleteness(config, cableType) {
  switch (cableType) {
    case 'base':
      return !!(config.length && config.length > 0);
    
    case 'hybrid':
    case 'hybrid-power-audio-audio':
      if (!config.hybridConnectors?.lengths) return false;
      const lengths = config.hybridConnectors.lengths;
      return !!(lengths.A && lengths.A > 0);
    
    case 'wye':
    case 'zip':
      // For wye and zip, check if we have length data
      return !!(config.length && config.length > 0) || 
             !!(config.hybridConnectors?.lengths?.A && config.hybridConnectors.lengths.A > 0);
    
    default:
      return false;
  }
}

/**
 * Checks if finishing is properly configured
 * @param {Object} config - The configuration object
 * @returns {boolean} - True if finishing is configured
 */
function checkFinishingCompleteness(config) {
  // Finishing is optional, so we consider it complete if it exists (even if empty)
  return !!(config.finishing || config.finishes);
}

/**
 * Loads a configuration from the database by hash
 * @param {string} hash - The configuration hash
 * @returns {Promise<Object|null>} - The configuration object or null if not found
 */
export async function loadConfigByHash(hash) {
  try {
    // Import the service dynamically to avoid circular dependencies
    const { default: savedConfigsService } = await import('./savedConfigsService');
    const config = await savedConfigsService.loadConfig(hash);
    return config;
  } catch (error) {
    console.error('Error loading config by hash:', error);
    return null;
  }
}

/**
 * Gets the appropriate connector selection component name for the cable type
 * @param {string} cableType - The cable type
 * @returns {string} - The component name
 */
export function getConnectorComponentName(cableType) {
  switch (cableType) {
    case 'hybrid':
      return 'HybridConnectorSelection';
    case 'wye':
      return 'WyeConnectorSelection';
    case 'zip':
      return 'ZipAudioAudioConnectorSelection';
    case 'hybrid-power-audio-audio':
      return 'HybridPowerAudioAudioConnectorSelection';
    case 'base':
    default:
      return 'BaseConnectorSelector';
  }
}

/**
 * Normalizes a configuration for the appropriate component
 * @param {Object} config - The configuration object
 * @param {string} cableType - The cable type
 * @returns {Object} - The normalized configuration
 */
export function normalizeConfigForComponent(config, cableType) {
  if (!config) return {};
  
  const normalized = { ...config };
  
  // Ensure the config has the right structure for the component
  switch (cableType) {
    case 'base':
      // Ensure we have endA and endB structure
      if (!normalized.connectors) {
        normalized.connectors = { endA: {}, endB: {} };
      }
      break;
    
    case 'hybrid':
    case 'hybrid-power-audio-audio':
      // Ensure we have hybridConnectors structure
      if (!normalized.hybridConnectors) {
        normalized.hybridConnectors = {
          connectors: { F: {}, G: {}, H: {}, I: {} },
          lengths: { A: 36, B: 12, C: 12, D: 12, E: 12, Overall: 60 }
        };
      }
      break;
    
    case 'wye':
    case 'zip':
      // Ensure we have the right connector structure
      if (!normalized.connectors) {
        normalized.connectors = {};
      }
      break;
  }
  
  return normalized;
}

/**
 * Main function to load and analyze a configuration from URL
 * @param {string} configHash - The configuration hash from URL
 * @returns {Promise<Object>} - Analysis result with cableType, step, and normalized config
 */
export async function loadAndAnalyzeConfig(configHash) {
  const config = await loadConfigByHash(configHash);
  
  if (!config) {
    return {
      success: false,
      error: 'Configuration not found',
      cableType: 'base',
      step: 0,
      config: {}
    };
  }
  
  const cableType = determineCableType(config);
  const step = determineStepFromConfig(config, cableType);
  const normalizedConfig = normalizeConfigForComponent(config, cableType);
  const componentName = getConnectorComponentName(cableType);
  
  return {
    success: true,
    cableType,
    step,
    config: normalizedConfig,
    componentName,
    originalConfig: config
  };
}
